/* iter230 · Post-Unlock-Modal (User-Vorgabe: Klick aufs geblurrte Bild → Verkaufsübersicht)
 *
 * Zeigt je nach Post-Modus Optionen:
 * - SINGLE_BUY       → nur „In den Warenkorb"
 * - SUBSCRIBERS_ONLY → nur „Premium-Abo abschließen"
 * - PPV              → beide Optionen
 *
 * Kein Extra-Freischalten-Button mehr unter dem Post — der Modal ist die
 * einzige Kauf-Route. Preview-Bild ist der Trigger.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, ShoppingCart, Sparkles, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PostUnlockModal({ open, post, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !post?.paywall) return null;

  const paywall = post.paywall;
  const mode = paywall.mode;  // SINGLE_BUY | SUBSCRIBERS_ONLY | PPV
  const price = Number(paywall.price_eur || 0);
  const assetId = paywall.paid_media_asset_id;
  const authorHandle = post.author?.handle || post.author_handle;

  const requireLogin = () => {
    if (!user) {
      navigate("/login?next=/");
      return true;
    }
    return false;
  };

  const handleBuy = async () => {
    if (requireLogin() || !assetId) return;
    // iter238 · Purchase-Consent-Gate: Modal-User bekommt Warnung wenn Checkbox
    // fehlt — 422 wird eh vom Backend abgefangen. Consent wird beim Checkout
    // im CartCheckoutPage bestätigt.
    try {
      await api.post("/cart/paid-media", { asset_id: assetId });
      navigate("/cart");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "ALREADY_OWNED") { window.location.reload(); return; }
      alert(typeof detail === "string" ? detail : "In den Warenkorb fehlgeschlagen");
    }
  };

  const handleSubscribe = () => {
    if (requireLogin()) return;
    // iter233 · Route ist /user/{handle} (nicht /creator/) — Bug „leere Seite"
    // nach Modal-Klick behoben.
    if (authorHandle) navigate(`/user/${authorHandle}?tab=abo`);
    else if (post.author_id) navigate(`/user/${post.author_id}?tab=abo`);
    else onClose?.();
  };

  return (
    <div
      data-testid="post-unlock-modal"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0F0F0]">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-[#0A0A0A]" />
            <p className="text-sm font-semibold text-[#0A0A0A]">
              {mode === "SINGLE_BUY" && "Einzelkauf"}
              {mode === "SUBSCRIBERS_ONLY" && "Premium-Inhalt"}
              {mode === "PPV" && "Freischalten"}
            </p>
          </div>
          <button
            type="button"
            data-testid="post-unlock-modal-close"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F5F5]"
            aria-label={t("common.close")}
          >
            <X size={18} className="text-[#666]" />
          </button>
        </div>

        {/* Post-Info */}
        <div className="px-5 py-4">
          {post.body_text && (
            <p className="text-sm text-[#333] line-clamp-3 mb-3">{post.body_text}</p>
          )}
          <p className="text-xs uppercase tracking-wider text-[#999] mb-1">
            von {post.author?.display_name || post.author?.handle || "Creator"}
          </p>
        </div>

        {/* Options */}
        <div className="px-5 pb-5 space-y-2">
          {(mode === "SINGLE_BUY" || mode === "PPV") && assetId && (
            <button
              type="button"
              data-testid="post-unlock-buy-btn"
              onClick={handleBuy}
              className="w-full h-14 rounded-2xl bg-[#0A0A0A] text-white flex items-center justify-between px-5 hover:bg-[#222] transition-colors"
            >
              <div className="flex items-center gap-3">
                <ShoppingCart size={18} />
                <div className="text-left">
                  <p className="text-sm font-semibold">Einzelkauf</p>
                  <p className="text-[10px] opacity-70">Einmalig · dauerhaft freigeschaltet</p>
                </div>
              </div>
              <span className="text-base font-bold">{price.toFixed(2)} €</span>
            </button>
          )}

          {(mode === "SUBSCRIBERS_ONLY" || mode === "PPV") && (
            <button
              type="button"
              data-testid="post-unlock-subscribe-btn"
              onClick={handleSubscribe}
              className={`w-full h-14 rounded-2xl flex items-center justify-between px-5 transition-colors ${
                mode === "PPV"
                  ? "border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#FAFAFA]"
                  : "bg-[#0A0A0A] text-white hover:bg-[#222]"
              }`}
            >
              <div className="flex items-center gap-3">
                <Sparkles size={18} />
                <div className="text-left">
                  <p className="text-sm font-semibold">Premium-Abo</p>
                  <p className="text-[10px] opacity-70">Zugriff auf alle Premium-Inhalte</p>
                </div>
              </div>
              <span className="text-xs opacity-70">Zum Abo →</span>
            </button>
          )}

          <p className="text-[10px] text-[#999] text-center pt-2">
            Sichere Zahlung · 14 Tage gesetzliches Widerrufsrecht
          </p>
        </div>
      </div>
    </div>
  );
}
